import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

/** Friendly copy for the `?error=` codes the Slack OAuth callback redirects with (see src/routes/slack.ts). */
export const SLACK_OAUTH_ERROR_MESSAGES: Record<string, string> = {
  missing_params: 'Slack install failed: the request was incomplete. Please try again.',
  invalid_state: 'Slack install failed: the request expired or was invalid. Please start the install again.',
  oauth_failed: 'Slack authorization failed. Please try again.',
  enterprise_not_supported: 'Slack Enterprise Grid is not supported yet.',
  internal: 'Something went wrong while installing Slack. Please try again.',
};

/**
 * Surfaces the Slack OAuth outcome the callback lands on — `?success=true` or
 * `?error=<code>` — as a toast, then strips the query so a refresh doesn't
 * re-fire it. The callback lands directly on /settings/integrations/slack (the
 * Slack tab), preserving the query.
 */
export function useSlackOAuthResultToast(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const shownFor = useRef<string | null>(null);

  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const key = success ? `success:${success}` : error ? `error:${error}` : null;
    if (!key || shownFor.current === key) return;
    shownFor.current = key;

    if (success) {
      toast.success('Slack connected.');
    } else if (error) {
      toast.error(SLACK_OAUTH_ERROR_MESSAGES[error] ?? 'Slack install failed. Please try again.');
    }

    const next = new URLSearchParams(searchParams);
    next.delete('success');
    next.delete('error');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
}
